import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SalonCard from '../components/SalonCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Search, Filter, X, MapPin } from 'lucide-react';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function SalonListing() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, setMenTheme, setWomenTheme, setNeutralTheme } = useTheme();
  
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [city, setCity] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [priceRange, setPriceRange] = useState([0, 500]);

  useEffect(() => {
    if (category === 'men') {
      setMenTheme();
    } else if (category === 'women') {
      setWomenTheme();
    } else {
      setNeutralTheme();
    }
  }, [category]);

  useEffect(() => {
    fetchSalons();
  }, [category, search, city, minRating, priceRange]);

  const fetchSalons = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (city) params.append('city', city);
      if (minRating > 0) params.append('min_rating', minRating);
      if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
      if (priceRange[1] < 500) params.append('max_price', priceRange[1]);

      const response = await fetch(`${API}/salons?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSalons(data);
      }
    } catch (error) {
      console.error('Failed to fetch salons:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCity('');
    setMinRating(0);
    setPriceRange([0, 500]);
    setSearchParams({});
  };

  const isMen = category === 'men' || theme === 'men';
  const isWomen = category === 'women' || theme === 'women';

  const bgClass = isMen ? 'bg-zinc-950' : isWomen ? 'bg-rose-50' : 'bg-zinc-50';
  const textClass = isMen ? 'text-white' : isWomen ? 'text-rose-900' : 'text-zinc-900';
  const secondaryTextClass = isMen ? 'text-zinc-400' : isWomen ? 'text-rose-600' : 'text-zinc-600';
  const cardBgClass = isMen ? 'bg-zinc-900 border-zinc-800' : isWomen ? 'bg-white border-pink-100' : 'bg-white border-zinc-200';
  const inputClass = isMen 
    ? 'bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500' 
    : isWomen 
      ? 'bg-white border-pink-200 text-rose-900 placeholder:text-pink-400'
      : 'bg-white border-zinc-300 text-zinc-900';

  const pageTitle = category === 'men' 
    ? "Men's Salons" 
    : category === 'women' 
      ? "Women's Salons" 
      : "All Salons";

  return (
    <div className={`min-h-screen ${bgClass}`}>
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-4xl md:text-5xl font-bold ${textClass} ${isMen ? 'font-heading-men uppercase tracking-wider' : isWomen ? 'font-heading-women italic' : ''}`} data-testid="listing-title">
            {pageTitle}
          </h1>
          <p className={`mt-2 ${secondaryTextClass}`}>
            {salons.length} {salons.length === 1 ? 'salon' : 'salons'} found
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${secondaryTextClass}`} />
            <Input
              type="text"
              placeholder="Search salons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-12 py-3 ${inputClass}`}
              data-testid="search-input"
            />
          </div>

          {/* City Filter */}
          <div className="relative w-full lg:w-48">
            <MapPin className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${secondaryTextClass} z-10`} />
            <Input
              type="text"
              placeholder="City..."
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={`pl-12 py-3 ${inputClass}`}
              data-testid="city-filter"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`lg:hidden ${isMen ? 'border-zinc-700 text-white' : ''}`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Filters Panel */}
        <div className={`${showFilters ? 'block' : 'hidden'} lg:block mb-8`}>
          <div className={`p-6 rounded-xl border ${cardBgClass}`} data-testid="filters-panel">
            <div className="flex flex-wrap gap-6 items-end">
              {/* Rating Filter */}
              <div className="w-full sm:w-auto">
                <label className={`block text-sm font-medium ${textClass} mb-2`}>
                  Minimum Rating
                </label>
                <Select value={minRating.toString()} onValueChange={(v) => setMinRating(Number(v))}>
                  <SelectTrigger className={`w-full sm:w-40 ${inputClass}`} data-testid="rating-filter">
                    <SelectValue placeholder="Any rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Any rating</SelectItem>
                    <SelectItem value="3">3+ stars</SelectItem>
                    <SelectItem value="4">4+ stars</SelectItem>
                    <SelectItem value="4.5">4.5+ stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div className="w-full sm:w-64">
                <label className={`block text-sm font-medium ${textClass} mb-2`}>
                  Price Range: ${priceRange[0]} - ${priceRange[1]}
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={500}
                  step={10}
                  className="mt-4"
                  data-testid="price-filter"
                />
              </div>

              {/* Clear Filters */}
              <Button
                variant="ghost"
                onClick={clearFilters}
                className={`${secondaryTextClass}`}
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Salon Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div 
                key={i} 
                className={`h-80 rounded-xl animate-pulse ${isMen ? 'bg-zinc-800' : 'bg-zinc-200'}`} 
              />
            ))}
          </div>
        ) : salons.length === 0 ? (
          <div className={`text-center py-20 ${textClass}`} data-testid="no-salons">
            <p className="text-xl font-medium mb-2">No salons found</p>
            <p className={secondaryTextClass}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="salons-grid">
            {salons.map((salon) => (
              <SalonCard key={salon.salon_id} salon={salon} theme={category || theme} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
